import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GeodataModel as GeodataModelV0 } from '../../../../../typings/api/skymap/rest/v0/.common';
import { GeodataModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { UserStore } from '../../../../js/stores/user-store';
import { formattedDateTime } from '../../../../js/utils/dateUtils';
import { toHumanReadableFileSize } from '../../../../js/utils/io/file';
import { isDefined } from '../../../../js/utils/variables';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { translatedProcessType } from '../geodata';

interface Props {
  selectedItem: GeodataModel;
  loadedItem?: GeodataModelV0;
}

const GeodataDetails = (props: Props) => {
  const { t } = useTranslation();
  const uploadedByName = useMemo(() => {
    if (!isDefined(props.loadedItem?.uploadedBy)) {
      return undefined;
    }

    return `${props.loadedItem.uploadedBy.firstName} ${props.loadedItem.uploadedBy.lastName}`;
  }, [props.loadedItem]);
  const filteredPointCloudInfo = useMemo(() => {
    if (!isDefined(props.loadedItem?.filteredPointCloud)) {
      return undefined;
    }

    return `${props.loadedItem.filteredPointCloud.format} (${props.loadedItem.filteredPointCloud.spacing}, ${
      props.loadedItem.filteredPointCloud.class === 'GROUND'
        ? t('setupDialog.addOns.filteredPointCloud.classesGround', { ns: 'cloudProcessing' })
        : t('setupDialog.addOns.filteredPointCloud.classesAll', { ns: 'cloudProcessing' })
    })`;
  }, [props.loadedItem, t]);

  const isOrthoProcess = ['ORTHO_ONLY', 'ORTHO_TIF'].includes(props.selectedItem.processType);
  const size = props.selectedItem.size ?? 0;
  const sizeGlacier = props.selectedItem.sizeGlacier ?? 0;

  return (
    <OverlayLoader visible={!isDefined(props.loadedItem)}>
      <Component>
        <h5>Detaljer</h5>
        <InfoRow>
          <InfoItem>
            <label id="process-type">Processeringspaket</label>
            <span aria-labelledby="process-type">
              {translatedProcessType(props.selectedItem.processType)}
            </span>
          </InfoItem>

          {(props.selectedItem.processType !== 'ORTHO_TIF' ||
            !props.selectedItem.isLegacyProcess) && (
            <>
              <InfoItem>
                <label id="photo-count">Antal foton</label>
                <span aria-labelledby="photo-count">{props.selectedItem.photoCount}</span>
              </InfoItem>

              {!props.selectedItem.isLegacyProcess && (
                <InfoItem>
                  <label id="photo-count">Antal GCP</label>
                  <span aria-labelledby="photo-count">
                    {props.loadedItem?.gcpCollection?.points.length ?? 0}
                  </span>
                </InfoItem>
              )}
            </>
          )}
        </InfoRow>

        {size + sizeGlacier > 0 && (
          <InfoRow>
            <InfoItem>
              <label id="size">Total storlek</label>
              <span aria-labelledby="size">{toHumanReadableFileSize(size + sizeGlacier)}</span>
            </InfoItem>
            {UserStore.instance.isOrganizationAdmin() && (
              <InfoItem>
                <label id="size-glacier">Varav glacier</label>
                <span aria-labelledby="size-glacier">{toHumanReadableFileSize(sizeGlacier)}</span>
              </InfoItem>
            )}
          </InfoRow>
        )}

        <InfoRow>
          <InfoItem>
            <label id="uploaded">Uppladdad</label>
            <span aria-labelledby="uploaded">{formattedDateTime(props.selectedItem.uploaded)}</span>
          </InfoItem>

          {uploadedByName && (
            <InfoItem>
              <label id="uploaded-by">Uppladdad av</label>
              <span aria-labelledby="uploaded-by">{uploadedByName}</span>
            </InfoItem>
          )}
        </InfoRow>
        <InfoRow>
          <InfoItem>
            <label id="boundary-file">Begränsningspolygon</label>
            <span aria-labelledby="boundary-file">{props.selectedItem.boundaryFile ?? 'Nej'}</span>
          </InfoItem>

          {props.selectedItem.processType !== 'ORTHO_TIF' && (
            <InfoItem>
              <label id="gsd">GSD</label>
              <span aria-labelledby="gsd">{props.selectedItem.gsd}</span>
            </InfoItem>
          )}

          <InfoItem>
            <label id="rolling-shutter">Rolling shutter</label>
            <span aria-labelledby="rolling-shutter">
              {props.selectedItem.rollingShutter ? 'Ja' : 'Nej'}
            </span>
          </InfoItem>
        </InfoRow>

        <h5>Projektion</h5>

        {props.loadedItem && (
          <InfoRow>
            <InfoItem>
              <label id="coordinate-system">Koordinatsystem</label>
              <span aria-labelledby="coordinate-system">
                {props.loadedItem.coordinateSystem?.name}
              </span>
            </InfoItem>

            <InfoItem>
              <label id="geoid">Geoid</label>
              <span aria-labelledby="geoid">{props.loadedItem.geoid?.name ?? 'Ingen geoid'}</span>
            </InfoItem>
          </InfoRow>
        )}

        {!isOrthoProcess && (
          <>
            <h5>Tilläggspaket</h5>
            <InfoRow>
              <InfoItem>
                <label id="gcp-correction">GCP-justering beställd</label>
                <span aria-labelledby="gcp-correction">
                  {props.loadedItem?.gcpAdjustmentRequest ? 'Ja' : 'Nej'}
                </span>
              </InfoItem>

              {(!isOrthoProcess || !props.selectedItem.isLegacyProcess) && (
                <>
                  <InfoItem>
                    <label id="terrain-model">Markmodell</label>
                    <span aria-labelledby="terrain-model">
                      {props.selectedItem.terrainModel ? 'Ja' : 'Nej'}
                    </span>
                  </InfoItem>

                  {isDefined(props.loadedItem) && (
                    <InfoItem>
                      <label id="filtered-pointcloud">Export av utglesat punktmoln</label>
                      <span aria-labelledby="filtered-pointcloud">
                        {filteredPointCloudInfo ?? 'Nej'}
                      </span>
                    </InfoItem>
                  )}
                </>
              )}
            </InfoRow>
          </>
        )}
      </Component>
    </OverlayLoader>
  );
};

const Component = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const InfoItem = styled.div`
  width: 30%;

  label {
    font-weight: 400;
    font-size: 0.9em;
    display: block;
    margin-bottom: 0.2em;
  }

  span {
    font-weight: 300;
    display: block;
    font-weight: 300;
    font-size: 0.9em;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
`;

export { GeodataDetails };
